<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  fieldGroup: {
    type: Object,
    default: () => ({}),
  },
  groupName: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['insert'])

const searchValue = ref('')
const expandedGroups = ref(['all'])
const copiedPath = ref(null)
let copiedTimer = null

// Filter fields based on search
const filteredFields = computed(() => {
  if (!props.fieldGroup?.fields) return []

  if (!searchValue.value) {
    return props.fieldGroup.fields
  }

  const search = searchValue.value.toLowerCase()
  
  return props.fieldGroup.fields.filter(field => field.label.toLowerCase().includes(search)
    || field.path.toLowerCase().includes(search))
})

// Group fields by category (first part of path after options.)
const groupedFields = computed(() => {
  const groups = {}

  filteredFields.value.forEach(field => {
    // Extract category from path (e.g., "options.user.firstName" -> "user")
    const parts = field.path.split('.')
    let category = 'other'

    if (parts[0] === 'options' && parts.length > 1) {
      category = parts[1]
    } else if (parts[0] === 'preheader') {
      category = 'system'
    } else if (parts[0] === 'process') {
      category = 'system'
    } else if (parts[0] === 'misc' || parts[0] === 'moment') {
      category = 'helpers'
    }

    if (!groups[category]) {
      groups[category] = []
    }
    groups[category].push(field)
  })

  return groups
})

// Copy field to clipboard (LiquidJS output syntax)
const copyToClipboard = async field => {
  const templateSyntax = `{{ ${field.path} }}`
  try {
    await navigator.clipboard.writeText(templateSyntax)
  } catch (err) {
    console.error('Failed to copy field:', err)

    return
  }
  copiedPath.value = field.path
  if (copiedTimer) clearTimeout(copiedTimer)
  copiedTimer = setTimeout(() => {
    copiedPath.value = null
  }, 1500)
}

// Get category display name
const getCategoryName = category => {
  const names = {
    user: 'User Fields',
    reservation: 'Reservation Fields',
    restaurant: 'Restaurant Fields',
    group: 'Group Fields',
    userGroup: 'User Group Fields',
    system: 'System Fields',
    helpers: 'Helper Functions',
    holdData: 'Payment Hold Fields',
    inviter: 'Inviter Fields',
    gifter: 'Gifter Fields',
    partner: 'Partner Fields',
    other: 'Other Fields',
  }
  
  return names[category] || category.charAt(0).toUpperCase() + category.slice(1)
}
</script>

<template>
  <div class="field-inserter">
    <div class="field-inserter-header">
      <h4 class="mb-2">
        Available Fields
      </h4>
      <p class="text-caption text-grey mb-2">
        Click a field to insert it at your cursor in the editor (copy icon copies it instead)
      </p>
      <VTextField
        v-model="searchValue"
        density="compact"
        variant="outlined"
        placeholder="Search fields..."
        prepend-inner-icon="mdi-magnify"
        hide-details
        clearable
        class="mb-3"
      />
    </div>

    <div class="field-inserter-content">
      <VExpansionPanels
        v-model="expandedGroups"
        multiple
      >
        <VExpansionPanel
          v-for="(fields, category) in groupedFields"
          :key="category"
          :value="category"
        >
          <VExpansionPanelTitle class="text-subtitle-2">
            {{ getCategoryName(category) }}
            <span class="text-caption text-grey ml-2">({{ fields.length }})</span>
          </VExpansionPanelTitle>
          <VExpansionPanelText>
            <VList
              density="compact"
              class="field-list"
            >
              <VListItem
                v-for="field in fields"
                :key="field.path"
                class="field-item"
                @click="emit('insert', field)"
              >
                <template #prepend>
                  <VIcon
                    size="small"
                    color="primary"
                  >
                    mdi-code-tags
                  </VIcon>
                </template>
                <VListItemTitle class="field-label">
                  {{ field.label }}
                </VListItemTitle>
                <VListItemSubtitle class="field-path">
                  {{ field.path }}
                </VListItemSubtitle>
                <template #append>
                  <span
                    v-if="copiedPath === field.path"
                    class="text-caption text-success font-weight-medium"
                  >
                    Copied!
                  </span>
                  <VBtn
                    v-else
                    icon="mdi-content-copy"
                    size="small"
                    variant="text"
                    color="grey"
                    title="Copy to clipboard instead"
                    @click.stop="copyToClipboard(field)"
                  />
                </template>
              </VListItem>
            </VList>
          </VExpansionPanelText>
        </VExpansionPanel>
      </VExpansionPanels>

      <div
        v-if="Object.keys(groupedFields).length === 0"
        class="text-center py-4"
      >
        <p class="text-grey">
          No fields found
        </p>
      </div>
    </div>

    <div class="field-inserter-footer mt-3">
      <p class="text-caption text-grey">
        <strong>Tip:</strong> Use <code>&lt;%= field %&gt;</code> for output,
        <code>&lt;% code %&gt;</code> for logic
      </p>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.field-inserter {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.field-inserter-header {
  flex-shrink: 0;
}

.field-inserter-content {
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
}

.field-list {
  padding: 0;
}

.field-item {
  cursor: pointer;
  border-radius: 4px;
  margin-bottom: 2px;

  &:hover {
    background-color: rgba(var(--v-theme-primary), 0.08);
  }
}

.field-label {
  font-size: 13px !important;
  font-weight: 500;
}

.field-path {
  font-family: monospace;
  font-size: 11px !important;
  color: var(--ds-text-muted);
}

.field-inserter-footer {
  flex-shrink: 0;
  border-top: 1px solid var(--ds-border-subtle);
  padding-top: 8px;

  code {
    background-color: var(--ds-surface-subtle);
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 11px;
  }
}
</style>
